<script setup lang="ts">
import { ref, watch } from 'vue'
import ObjectPicker from '@/onenetwork/components/object_picker/ObjectPicker.vue'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant'
import EstateProjectAPI from '@/modules/estate-project/constants/estate-project-api-uri-const'
import InvestorQuickCreateForm from '@/modules/estate-project/components/investor-quick-create-form.vue'

interface InvestorOption {
  id: string
  name: string
  logo?: string
  description?: string
}

interface InvestorFormLike {
  initialValues?: {
    investor?: InvestorOption | null
  }
  setFieldValue?: (field: string, value: InvestorOption | null) => void
  fields?: {
    investor?: {
      states?: {
        error?: {
          message?: string
        }
      }
    }
  }
}

interface Props {
  form: InvestorFormLike
  editable: boolean
}

const props = defineProps<Props>()

const selected = ref<InvestorOption[]>([])
const investors = ref<InvestorOption[]>([])
const drawerVisible = ref(false)

const toRecord = (value: unknown): Record<string, unknown> => {
  if (typeof value === 'object' && value !== null) return value as Record<string, unknown>
  return {}
}

const normalizeOwnerList = (payload: unknown): InvestorOption[] => {
  const safePayload = toRecord(payload)
  const root = toRecord(safePayload.data)
  const nested = toRecord(root.data)

  const rawList = Array.isArray(root.data)
    ? root.data
    : Array.isArray(nested.data)
      ? nested.data
      : Array.isArray(nested.items)
        ? nested.items
        : Array.isArray(root.items)
          ? root.items
          : []

  return rawList
    .map((item: any) => ({
      id: String(item?.id || ''),
      name: String(item?.name || item?.title || ''),
      logo: item?.logo,
      description: item?.description,
    }))
    .filter((item) => item.id && item.name)
}

const fetchInvestors = async () => {
  try {
    const res = await APIClient.get(EstateProjectAPI.investor.list)
    investors.value = normalizeOwnerList(res)
  } catch {
    investors.value = []
  }
}

const syncInvestorField = () => {
  const investor = selected.value[0] || null
  props.form?.setFieldValue('investor', investor)
}

const handleSelectInvestor = (vals: InvestorOption[]) => {
  selected.value = vals || []
  syncInvestorField()
}

const handleCreatedInvestor = async (investor: InvestorOption) => {
  await fetchInvestors()
  selected.value = [{ id: investor.id, name: investor.name }]
  syncInvestorField()
  drawerVisible.value = false
}

watch(
  () => props.form?.initialValues?.investor,
  async (investor?: InvestorOption | null) => {
    await fetchInvestors()
    if (investor?.id) {
      selected.value = [{ id: investor.id, name: investor.name }]
      syncInvestorField()
      return
    }
    selected.value = []
  },
  { immediate: true },
)
</script>

<template>
  <div class="estate-project-section-body">
    <InputText type="text" name="investor" hidden />

    <div class="estate-project-section-header">
      <label class="estate-project-section-title">Chủ đầu tư dự án <span>*</span></label>
      <button
        type="button"
        class="estate-project-section-link"
        v-if="editable"
        @click="drawerVisible = true"
      >
        + Thêm chủ đầu tư mới
      </button>
    </div>

    <ObjectPicker
      :read-only="!editable"
      placeholder="Chọn chủ đầu tư"
      :multiple="false"
      :modelValue="selected"
      key_field="id"
      display_field="name"
      query-field="name"
      api-url=""
      :data="investors"
      @update:selected="handleSelectInvestor"
    />

    <span class="estate-project-error">{{ props.form?.fields?.investor?.states?.error?.message }}</span>

    <Drawer
      v-model:visible="drawerVisible"
      header="Thêm mới chủ đầu tư"
      position="right"
      :style="{ width: 'min(26vw, 360px)' }"
    >
      <div class="investor-create-drawer-body">
        <InvestorQuickCreateForm @created="handleCreatedInvestor" />
      </div>
    </Drawer>
  </div>
</template>

<style scoped>
.estate-project-section-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 7px 0;
}

.estate-project-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.estate-project-section-title {
  font-size: 16px;
  font-weight: 700;
  color: #000;
}

.estate-project-section-title span {
  color: #ff0000;
}

.estate-project-section-link {
  font-size: 16px;
  font-weight: 700;
  color: #0035ba;
  border: 0;
  background: transparent;
  padding: 0;
}

:deep(.p-select),
:deep(.p-inputtext),
:deep(.object-picker),
:deep(.multiselect) {
  background: #f7f7f7 !important;
  border: 1px solid #f7f7f7 !important;
  border-radius: 4px !important;
}

:deep(.object-picker .multiselect__tags),
:deep(.object-picker .multiselect__single),
:deep(.object-picker .multiselect__placeholder) {
  font-size: 12px !important;
  color: #777777 !important;
}

.investor-create-drawer-body {
  padding-top: 4px;
}

.estate-project-error {
  display: block;
  font-size: 12px;
  font-style: italic;
  color: #dc2626;
}
</style>
